import { EMBED_CHAT_REQUEST_HEADERS } from "@nexus/types/api-envelope"

/**
 * Which host page the widget is embedded on — read from inside the frame.
 *
 * The customer pastes a `<script src="{backendUrl}/api/deployments/<id>/widget.js">`
 * onto their page and that script injects a CROSS-ORIGIN iframe at
 * `{frontendBaseUrl}/Embed/<id>`. Every chat request is therefore issued by OUR
 * document, so the `Origin` header the backend sees is always the Nexus
 * frontend host and never the customer's page. `location.ancestorOrigins`, with
 * `document.referrer` as a fallback, is the only thing inside the frame that
 * can name the page we are embedded on.
 *
 * 🔴 THIS IS TELEMETRY AND IT MUST NEVER GATE OR REFUSE ANYTHING. Two
 * independent, customer-controlled mechanisms produce a legitimate empty
 * reading on the customer's own site: `Location.ancestorOrigins` is absent on
 * Firefox before 148, and `document.referrer` is blanked by any host page
 * sending `Referrer-Policy: no-referrer` or `same-origin`. Every read below is
 * therefore total — every failure, every absence and every malformed value
 * resolves to `undefined`, which the caller encodes as an ABSENT header. An
 * empty string, `"null"` or `"unknown"` would all be lies the server cannot
 * distinguish from a real reading.
 *
 * debt: the in-frame ancestor reading is telemetry, never a refusal.
 *       Ceiling: it detects unauthorised framing only where the visitor's
 *       browser supplies location.ancestorOrigins (Firefox >= 148, Chrome,
 *       Safari) or the host page leaves document.referrer unblanked. It gates
 *       nothing; a fail-closed reading would refuse legitimate visitors on the
 *       customer's own site.
 *       Upgrade trigger: Firefox ESR ships >= 148 AND the layer-3 telemetry
 *       shows a non-empty reading on >99% of mints for 30 days. Then it may
 *       become a gate on the paths frame-ancestors cannot reach.
 */

/**
 * The request header carrying the reading — the published contract's name for it.
 *
 * Read off `EMBED_CHAT_REQUEST_HEADERS` in `@nexus/types`, the list the backend's
 * CORS allowlist is checked against, so the name the client sends and the name
 * the backend admits are one string rather than two that can drift.
 */
export const EMBED_ANCESTOR_ORIGIN_HEADER = EMBED_CHAT_REQUEST_HEADERS.embedAncestorOrigin

/**
 * Everything the reading depends on, as untrusted input.
 *
 * `ancestorOrigins` and `referrer` are `unknown` rather than their DOM types on
 * purpose: this function's whole job is to survive a browser that does not
 * implement the first and a host page that blanks the second, so it must not
 * assume either has the shape the DOM lib declares.
 */
export interface AncestorOriginInputs {
  /** Whether the document genuinely has an ancestor browsing context. */
  readonly framed: boolean
  /** `location.ancestorOrigins` — `undefined` where the browser has no such property. */
  readonly ancestorOrigins: unknown
  /** `document.referrer` — `""` when the host page's Referrer-Policy blanks it. */
  readonly referrer: unknown
}

/** The only part of `DOMStringList` this reading uses. */
interface AncestorOriginsLike {
  readonly length: number
  item: (index: number) => string | null
}

/**
 * A structural guard rather than a cast: `DOMStringList` is not present at all
 * on browsers that do not implement `ancestorOrigins`, so the value has to be
 * proven to have the shape before it is indexed. Reading `[0]` off `undefined`
 * throws, and a throw here breaks chat for a real visitor on the customer's
 * own site — the exact failure this whole reading exists to avoid.
 */
function isAncestorOriginsLike(value: unknown): value is AncestorOriginsLike {
  return (
    typeof value === "object" &&
    value !== null &&
    "length" in value &&
    typeof value.length === "number" &&
    "item" in value &&
    typeof value.item === "function"
  )
}

/**
 * A usable origin, or nothing.
 *
 * `"null"` is the serialization of an OPAQUE origin — a sandboxed frame, a
 * `data:` document. It is a real value the platform hands back and it names no
 * host page, so it is dropped here rather than being forwarded as though it
 * were one.
 */
function normalizeOrigin(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined
  const trimmed = value.trim()
  if (trimmed === "" || trimmed === "null") return undefined
  return trimmed
}

/** The first ancestor origin, where the browser supplies the list at all. */
function fromAncestorOrigins(value: unknown): string | undefined {
  if (!isAncestorOriginsLike(value)) return undefined
  if (value.length < 1) return undefined
  return normalizeOrigin(value.item(0))
}

/**
 * The referrer's origin.
 *
 * `new URL("")` throws, and so does `new URL` on anything else that is not an
 * absolute URL, so the construction is guarded rather than the input
 * pre-validated — a check that tried to predict which strings the URL parser
 * accepts would be a second, worse parser.
 */
function fromReferrer(value: unknown): string | undefined {
  if (typeof value !== "string" || value === "") return undefined
  try {
    return normalizeOrigin(new URL(value).origin)
  } catch {
    return undefined
  }
}

/**
 * The origin of the page this document is embedded in, or `undefined`.
 *
 * Pure — it reads no globals, so it is exercised without a DOM.
 *
 * The `framed` gate is not an optimisation. This module is reached from shared
 * chat code that the console's own Playground and the Deployments embed
 * PREVIEW also run, and neither of those is in a frame: in an unframed console
 * tab `ancestorOrigins` is empty but `document.referrer` is whatever page
 * linked there, so an ungated fallback would report one of our OWN hosts as an
 * embedding page and poison the dataset the reading exists to produce.
 */
export function readAncestorOrigin({
  framed,
  ancestorOrigins,
  referrer
}: AncestorOriginInputs): string | undefined {
  if (!framed) return undefined
  return fromAncestorOrigins(ancestorOrigins) ?? fromReferrer(referrer)
}

/**
 * {@link readAncestorOrigin} against the live document.
 *
 * The whole body is wrapped: this runs on the first request of every embed
 * chat session on every browser, and any throw at all here is a broken chat on
 * a customer's site. There is no failure mode but `undefined`.
 */
export function currentAncestorOrigin(): string | undefined {
  try {
    if (typeof window === "undefined" || typeof document === "undefined") return undefined

    // `ancestorOrigins` is not declared on `Location` in every TS DOM lib, and
    // is absent at RUNTIME on Firefox before 148. Widening through an optional
    // `unknown` property reads it without a cast and without assuming either.
    const location: { readonly ancestorOrigins?: unknown } = window.location

    return readAncestorOrigin({
      // `window.self !== window.top` is the repo's existing framing idiom
      // (`resolveEmbedData` in `chatApi.ts` uses it). Comparing the two window
      // proxies is legal cross-origin and reads TRUE for any depth of nesting.
      framed: window.self !== window.top,
      ancestorOrigins: location.ancestorOrigins,
      referrer: document.referrer
    })
  } catch {
    return undefined
  }
}
